import type { CalculatorArticleContent } from "../calculator-article";

export const calorieGuide: CalculatorArticleContent = {
  id: "calorie",
  eyebrow: "Enerji dengesi rehberi",
  updatedAt: "29 Eylül 2026",
  articleTitle: "Kalori ihtiyacını tahmin etmek ve doğru ayarlamak",
  intro:
    "Kalori hesaplayıcı, yaşın, boyun, vücut ağırlığın, cinsiyetin ve seçtiğin aktivite düzeyinden bir başlangıç tahmini üretir. Bu sayı laboratuvar ölçümü veya kişisel beslenme reçetesi değildir. En iyi kullanım biçimi, hesaplanan değeri iki ila dört haftalık ağırlık eğilimi, performans, açlık ve toparlanma verileriyle sınamaktır.",
  sections: [
    {
      id: "bmr-tdee-farki",
      title: "BMR, RMR ve TDEE ne anlama gelir?",
      paragraphs: [
        "BMR, vücudun tam dinlenme koşullarında temel işlevleri sürdürmek için harcadığı enerjiyi ifade eder. RMR daha az katı ölçüm koşullarında belirlenen dinlenme harcamasıdır ve günlük kullanımda iki terim sıkça birbirinin yerine kullanılır. TDEE ise dinlenme harcamasına günlük hareket, egzersiz ve besinlerin termik etkisini ekleyen toplam günlük enerji harcamasıdır.",
        "Bu araç dinlenme ihtiyacını Mifflin–St Jeor denklemiyle tahmin eder. Orijinal denklem, sağlıklı yetişkinlerden toplanan dolaylı kalorimetri verileriyle geliştirilmiştir. Erkekler için 10 × kilo + 6,25 × boy − 5 × yaş + 5; kadınlar için son sabit −161 olacak şekilde kullanılır.",
        "TDEE için bulunan dinlenme değeri seçtiğin aktivite katsayısıyla çarpılır. Bu katsayı günlük yaşamın basitleştirilmiş temsilidir; aynı sayıda antrenman yapan iki kişinin işi, adım sayısı ve antrenman yoğunluğu farklı olabilir. Bu nedenle TDEE, doğrulanması gereken bir başlangıç noktasıdır.",
      ],
      sourceIds: ["1", "2"],
    },
    {
      id: "aktivite-seviyesi",
      title: "Aktivite seviyesini gerçekçi seç",
      paragraphs: [
        "Aktivite düzeyi yalnız spor salonuna kaç gün gittiğin değildir. Masa başında çalışan ve haftada dört kez kısa antrenman yapan biriyle gün boyu ayakta çalışan, yüksek adım sayısına sahip biri aynı katsayıyı kullanmayabilir. Haftanın tamamına bak: iş, ulaşım, ev işleri, adım sayısı ve antrenman birlikte değerlendirilmelidir.",
        "Kararsız kaldığında daha düşük katsayıyla başlamak ve gerçek veriye göre ayarlamak genellikle daha pratiktir. Yüksek aktivite seçmek hedef kaloriyi gereksiz yükseltebilir; çok düşük seçmek ise performans ve toparlanmayı zorlayabilir. Katsayıyı kimlik etiketi gibi değil, test edilecek varsayım gibi düşün.",
      ],
      table: {
        caption: "Aktivite seçimini düşünürken bakılacak işaretler",
        headers: ["Durum", "Dikkate alınacak veri", "Yaygın hata"],
        rows: [
          [
            "Masa başı yaşam",
            "Adım sayısı ve antrenman dışı hareket",
            "Sadece antrenman gününe bakmak",
          ],
          [
            "Fiziksel iş",
            "Çalışma süresi, taşıma ve ayakta kalma",
            "İşi hesaba katmamak",
          ],
          [
            "Dayanıklılık sporu",
            "Haftalık süre ve yoğunluk",
            "Her seansı aynı enerji harcaması sanmak",
          ],
          [
            "Kuvvet antrenmanı",
            "Seans hacmi, dinlenme ve günlük hareket",
            "Salon süresini sürekli aktif süre kabul etmek",
          ],
        ],
      },
    },
    {
      id: "hedef-kalori",
      title: "Kilo verme veya alma hedefi nasıl kurulmalı?",
      paragraphs: [
        "Yağ kaybı için harcadığından daha az, kilo artışı için daha fazla enerji almak gerekir. Fakat matematiksel açık veya fazlanın kâğıt üzerinde bulunması, her gün aynı hızda değişim olacağı anlamına gelmez. Su, glikojen, sindirim sistemi içeriği ve hormonal değişimler tartıyı kısa vadede oynatır.",
        "Büyük bir açığı daha hızlı sonuç sanmak performans kaybı, yüksek açlık ve sürdürülemezlik yaratabilir. Fazlayı aşırı büyütmek de kas gelişimini aynı oranda hızlandırmaz. Başlangıç hedefini ölçülü seç, antrenman kalitesini ve uyumu izle, sonra küçük adımlarla değiştir.",
      ],
      steps: [
        "Hesaplayıcıdan çıkan TDEE değerini başlangıç kabul et.",
        "Hedefe göre ölçülü bir açık, bakım veya fazla seç.",
        "Her sabah benzer koşullarda tartılabiliyorsan haftalık ortalamayı takip et.",
        "En az iki haftalık eğilimi performans, açlık ve uyumla birlikte değerlendir.",
        "Beklenen yönde hareket yoksa günlük hedefi küçük bir miktar değiştir ve yeniden gözlemle.",
      ],
      note: "Günlük tek tartı sonucuna göre kalori değiştirme. Kısa süreli artış veya düşüş çoğu zaman yağ dokusundaki gerçek değişimi göstermez.",
    },
    {
      id: "veriyle-ayarlama",
      title: "Tahmini kişisel verinle kalibre et",
      paragraphs: [
        "Bir hesaplayıcının asıl değeri ilk sayıyı vermesi değil, düzenli takibi başlatmasıdır. Örneğin araç 2.500 kcal bakım tahmini verdi ve üç hafta boyunca ortalama 2.500 kcal tüketirken haftalık ortalama kilon belirgin biçimde düşüyorsa gerçek bakım ihtiyacın bu dönemde daha yüksek olabilir. Tersi durumda tahmin yüksek kalmış olabilir.",
        "Kalibrasyon için tüketim kaydının ve tartım koşullarının makul ölçüde tutarlı olması gerekir. Yağ, sos, içecek ve atıştırmaları atlamak raporlanan alımla gerçek alım arasında fark yaratır. Kusursuz kayıt gerekmese de karar verecek kadar istikrarlı veri gerekir.",
        "Vücut ağırlığın, hareket miktarın ve antrenman hacmin değiştikçe enerji ihtiyacın da değişebilir. Diyetin ilk haftasında oluşturduğun hedefi aylar boyunca sorgulamadan sürdürme. Gelişim durduğunda önce uyumu ve ölçüm hatasını, sonra hedef kaloriyi değerlendir.",
      ],
      sourceIds: ["2"],
    },
    {
      id: "kalori-tek-basina-yetmez",
      title: "Kalori hedefi beslenmenin tamamı değildir",
      paragraphs: [
        "Aynı kalori toplamı farklı protein, lif, mikro besin ve öğün dağılımlarıyla kurulabilir. Toplam enerji vücut ağırlığı yönünü güçlü biçimde etkiler; ancak performans, tokluk, sindirim ve sağlık için besin seçimi de önemlidir. Bu hesaplayıcı makro veya mikro besin gereksinimini tek başına belirlemez.",
        "Kuvvet antrenmanı yapan biri için yeterli protein ve karbonhidrat dağılımı, yalnız toplam kaloriye bakmaktan daha işlevsel olabilir. Bununla birlikte herhangi bir makro hedefi tıbbi durumdan bağımsız evrensel reçete değildir. Böbrek hastalığı, diyabet, gebelik, emzirme, yeme bozukluğu öyküsü veya özel beslenme ihtiyacında uzman değerlendirmesi gerekir.",
        "Uyku, stres ve program yükü de iştah ile toparlanmayı etkiler. Kilo eğilimi istediğin yönde olsa bile performans hızla düşüyor, sürekli yorgunluk veya kontrol edilemeyen açlık oluşuyorsa yalnız sayıyı korumak yerine planın sürdürülebilirliğini gözden geçir.",
      ],
    },
    {
      id: "yaygin-hatalar",
      title: "Sık yapılan hatalar ve daha iyi yaklaşım",
      paragraphs: [
        "Kalori hesabında en büyük sapma çoğu zaman denklemden değil, aktivite seçimi ve takip biçiminden gelir. Sayıyı kesin gerçek gibi görmek yerine aşağıdaki hataları azaltmaya çalış.",
      ],
      bullets: [
        "Yalnız antrenman sayısına bakıp günlük hareketi yok saymak.",
        "Hafta sonu tüketimini veya sıvı kalorileri kayda eklememek.",
        "Bir günlük tartı artışını yağ kazanımı kabul edip hedefi hemen düşürmek.",
        "Kilo kaybı hızını artırmak için açığı sürekli büyütmek.",
        "Aynı kalorinin performans ve tokluk açısından her besin dağılımında eşit olduğunu sanmak.",
        "Hastalık, gebelik veya yeme bozukluğu öyküsünde genel hesaplayıcıyı kişisel reçete yerine kullanmak.",
      ],
      sourceIds: ["1", "3"],
    },
    {
      id: "uygulama-ornegi",
      title: "Uygulama örneği: TDEE tahminini gerçek ihtiyaca çevirmek",
      paragraphs: [
        "Hesaplayıcının 2.600 kcal TDEE tahmini verdiğini ve yağ kaybı hedefiyle 2.300 kcal seçtiğini düşün. İlk hafta tartı ortalaman hızla düşebilir; bunun bir kısmı glikojen, su ve sindirim sistemi içeriğidir. Bu tek haftaya bakıp hedefi daha da azaltmak yerine en az bir sonraki haftayı aynı kayıt düzeniyle izle.",
        "Üç haftanın sonunda ortalama tüketimin gerçekten 2.300 kcal civarındaysa, haftalık ağırlık ortalaman düzenli fakat yönetilebilir biçimde düşüyor, antrenman performansın ve açlık düzeyin kabul edilebilir kalıyorsa başlangıç tahmini işlevini yerine getiriyor demektir. Planı değiştirmek için yalnız daha hızlı sonuç istemek yeterli neden değildir.",
        "Ağırlık eğilimi sabitse önce kaydın doğruluğuna bak: hafta sonu öğünleri, yağlar, içecekler ve porsiyon tahminleri dahil mi? Uyum yeterliyse hedefi küçük bir miktar azaltabilir veya sürdürülebilir günlük hareket ekleyebilirsin. Performans keskin düşüyorsa, sürekli açlık ve kötü uyku varsa daha büyük açık yerine toparlanmayı koruyan daha ılımlı plan düşün.",
        "Kilo alma hedefinde aynı süreç ters yönde işler. Hesaplanan fazlayı başlangıç kabul et; ağırlık çok hızlı artarken bel çevresi belirgin yükseliyorsa fazlayı küçült. Ağırlık ve kuvvet haftalar boyunca ilerlemiyorsa kayıt ile antrenman uyumunu kontrol ettikten sonra küçük artış yap. Bu yaklaşım hesaplayıcıyı sabit reçeteden kişisel geri bildirim döngüsüne dönüştürür.",
      ],
    },
    {
      id: "plato-ve-diyet-molasi",
      title: "Plato sandığın durumda önce neyi kontrol etmelisin?",
      paragraphs: [
        "Tartının birkaç gün sabit kalması plato değildir. Özellikle sert antrenman, yüksek karbonhidrat veya tuz, menstrual döngü ve kabızlık su ağırlığını geçici olarak artırabilir. Yağ kaybı sürerken tartı değişmeyebilir. Bu nedenle en az iki haftalık ortalama, bel çevresi ve kayıt uyumu birlikte incelenmelidir.",
        "Gerçek plato varsa ilk soru metabolizmanın 'bozulup bozulmadığı' değil, enerji dengesini etkileyen nelerin değiştiğidir. Vücut ağırlığı düştükçe dinlenme ve hareket maliyeti azalabilir; diyet sırasında farkında olmadan günlük hareket de düşebilir. Porsiyonların büyümesi veya kayıt dışı öğünler tahmini açığı kapatabilir.",
        "Kaloriyi yeniden azaltmadan önce günlük adım, antrenman performansı, uyku, açlık ve kayıt tutarlılığını kontrol et. Uyum yüksek ve eğilim gerçekten durmuşsa küçük bir enerji ayarı yeterli olabilir. Büyük kesintiler kısa vadede tartıyı oynatsa da antrenman kalitesini ve sürdürülebilirliği zayıflatabilir.",
      ],
      subsections: [
        {
          title: "Bakım dönemi veya diyet molası ne zaman düşünülür?",
          paragraphs: [
            "Uzun süren enerji açığında yüksek açlık, kötü uyku, performans düşüşü ve plan uyumunda bozulma oluşabilir. Kontrollü bakım dönemi psikolojik ve antrenman açısından nefes alanı sağlayabilir; ancak otomatik olarak yağ kaybını hızlandıran bir hile değildir. Süresi ve zamanlaması hedefe, diyet geçmişine ve yarışma takvimine göre planlanmalıdır.",
            "Bakım kalorisini de ilk gün kesin bilemezsin. Güncel vücut ağırlığı ve aktiviteyle yeni tahmin üret, ardından ağırlık eğilimiyle doğrula. Diyet öncesindeki kaloriye doğrudan dönmek mevcut bakım ihtiyacının üzerine çıkabilir.",
          ],
        },
        {
          title: "Performans hedefinde enerji kullanılabilirliği",
          paragraphs: [
            "Kuvvet veya kas gelişimi önceliğinde yalnız tartı hızına bakmak yeterli değildir. Tekrarlayan performans düşüşü, iyileşmeyen yorgunluk ve antrenman hacmine uyum sağlayamama enerji alımının yetersiz olabileceğine işaret edebilir. Bu belirtiler tıbbi nedenlere de bağlı olabileceği için uzun sürerse profesyonel değerlendirme gerekir.",
          ],
        },
      ],
      sourceIds: ["3"],
    },
  ],
  faqs: [
    {
      question: "TDEE sonucum her gün aynı mıdır?",
      answer:
        "Hayır. Adım sayısı, iş yükü, antrenman ve günlük hareket değiştikçe gerçek harcama da değişir. Araç ortalama bir başlangıç tahmini verir.",
    },
    {
      question: "Aktivite seviyesini nasıl seçmeliyim?",
      answer:
        "Antrenman sayısıyla birlikte işini, ulaşımını ve günlük adım sayını düşün. Kararsızsan daha temkinli seçip birkaç haftalık ağırlık eğilimine göre kalibre et.",
    },
    {
      question: "BMR kadar kalori almak güvenli midir?",
      answer:
        "BMR bir günlük beslenme hedefi değildir; dinlenme harcaması tahminidir. Uygun enerji alımı hedefe, aktiviteye ve sağlık durumuna göre değerlendirilmelidir.",
    },
    {
      question: "Kaloriyi ne zaman değiştirmeliyim?",
      answer:
        "Tutarlı kayıtla en az iki haftalık eğilim beklemek çoğu durumda günlük dalgalanmalara tepki vermekten daha anlamlıdır. Değişiklik yapacaksan küçük adımlarla ilerle.",
    },
    {
      question: "Kilo düşmüyorsa denklem yanlış mı?",
      answer:
        "Olabilir, fakat önce kayıt tutarlılığı, porsiyon tahmini, hafta sonu tüketimi ve aktivite değişimini kontrol et. Denklem zaten kişisel ölçüm değil, başlangıç tahminidir.",
    },
    {
      question: "Kas kazanmak için ne kadar fazla yemeliyim?",
      answer:
        "Tek bir evrensel miktar yoktur. Ölçülü bir fazla, antrenman performansı ve haftalık ağırlık eğilimiyle ayarlanmalıdır. Daha büyük fazla otomatik olarak daha fazla kas anlamına gelmez.",
    },
    {
      question: "Bu hesaplayıcı tıbbi beslenme planı oluşturur mu?",
      answer:
        "Hayır. Hastalık, gebelik, emzirme, yeme bozukluğu öyküsü veya özel beslenme gereksiniminde hekim ve diyetisyen değerlendirmesi gerekir.",
    },
    {
      question: "Kalori hesabı olmadan kilo yönetilebilir mi?",
      answer:
        "Evet. Porsiyon, öğün yapısı ve ağırlık eğilimi gibi yöntemler de kullanılabilir. Hesaplayıcı yalnız sayısal bir başlangıç aracı sunar.",
    },
    {
      question: "Egzersizde yaktığım kaloriyi tekrar yemeli miyim?",
      answer:
        "TDEE aktivite katsayısı egzersizi zaten yaklaşık olarak içerebilir. Saatin gösterdiği kaloriyi ayrıca eklemek çift sayım yaratabilir. Başlangıç hedefini ağırlık eğilimi ve performansla kalibre et.",
    },
  ],
  sources: [
    {
      id: "1",
      label:
        "Mifflin ve arkadaşları — dinlenme enerji harcaması için yeni denklem",
      href: "https://pubmed.ncbi.nlm.nih.gov/2305711/",
    },
    {
      id: "2",
      label:
        "Mifflin–St Jeor denkleminin dolaylı kalorimetriyle karşılaştırılması",
      href: "https://pubmed.ncbi.nlm.nih.gov/25826461/",
    },
    {
      id: "3",
      label: "National Academies — enerji dengesi ve enerji gereksinimleri",
      href: "https://www.ncbi.nlm.nih.gov/books/NBK56068/",
    },
  ],
  related: [
    {
      href: "/essay/body-fat-calculator",
      label: "Yağ oranını hesapla",
      description:
        "Kilo eğilimini vücut kompozisyonu tahminiyle birlikte izle.",
    },
    {
      href: "/essay/water-calculator",
      label: "Su ihtiyacını hesapla",
      description: "Günlük hidrasyon için pratik bir başlangıç hedefi oluştur.",
    },
  ],
  primaryCta: {
    href: "/coachs",
    label: "Koçluk seçeneklerini incele",
    description:
      "Beslenme ve antrenman sürecini kişisel verinle birlikte planla.",
  },
};
